/**
 * LandingPage - Single-page design (one-page)
 * All sections in a single scroll flow
 */
import React, { useState, useEffect } from 'react';
import { Box, Dialog, IconButton, Fab, Zoom, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Components
import Login from './login';
import PricingPlans from './PricingPlans';

// Landing components
import {
  LandingHeader,
  LandingHero,
  MetricsSection,
  TestimonialSection,
  ContactPeopleSection,
  IntegrationSection,
  FAQSection,
  PlatformSection,
  UseCasesSection,
  ChatPreviewSection,
  BeforeAfterSection,
  HowToStartSection,
  ComplianceSection,
  LandingFooter,
  COLORS,
} from './landing';

export default function LandingPage() {
  const [openLogin, setOpenLogin] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: COLORS.white,
        overflowX: 'hidden',
      }}
    >
      {/* Header / Navigation */}
      <LandingHeader />

      {/* Hero Section — has its own navy background */}
      <LandingHero onOpenLogin={() => setOpenLogin(true)} />

      {/* Metrics — capability numbers band */}
      <MetricsSection />

      {/* Before vs After — establish the problem */}
      <BeforeAfterSection />

      {/* Features — the solution */}
      <Box id="funcionalidades" sx={{ scrollMarginTop: 70 }}>
        <IntegrationSection />
      </Box>

      {/* Chat Preview — see it in action */}
      <ChatPreviewSection />

      {/* Use Cases — relevance by specialty */}
      <Box id="soluciones" sx={{ scrollMarginTop: 70 }}>
        <UseCasesSection />
      </Box>

      {/* Platform — depth for serious buyers */}
      <Box id="plataforma" sx={{ scrollMarginTop: 70 }}>
        <PlatformSection />
      </Box>

      {/* How to start — reduce friction */}
      <Box id="como-empezar" sx={{ scrollMarginTop: 70 }}>
        <HowToStartSection />
      </Box>

      {/* Pricing — decision point */}
      <Box id="precios" sx={{ scrollMarginTop: 70 }}>
        <PricingPlans />
      </Box>

      {/* Security & Compliance — final trust */}
      <ComplianceSection />

      {/* FAQ — last objections */}
      <Box id="faq" sx={{ scrollMarginTop: 70 }}>
        <FAQSection />
      </Box>

      {/* Tech stack — for the curious */}
      <Box id="tecnologia" sx={{ scrollMarginTop: 70 }}>
        <TestimonialSection />
      </Box>

      {/* Contactos humanos — cierre humano del funnel, previo al footer */}
      <ContactPeopleSection />

      {/* Login Modal */}
      <Dialog
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <IconButton
            onClick={() => setOpenLogin(false)}
            sx={{
              background: COLORS.gray100,
              '&:hover': { background: COLORS.gray200 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Login />
      </Dialog>

      {/* Footer — has its own navy background */}
      <LandingFooter />

      {/* Floating WhatsApp button */}
      <Tooltip title="Solicitar demo" placement="left">
        <Fab
          component="a"
          href="https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio"
          target="_blank"
          rel="noopener"
          aria-label="WhatsApp"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 56,
            height: 56,
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
            zIndex: 1200,
            '&:hover': {
              background: 'linear-gradient(135deg, #20bd5a 0%, #0f7a6e 100%)',
              boxShadow: '0 8px 28px rgba(37, 211, 102, 0.5)',
              transform: 'scale(1.08)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 28 }} />
        </Fab>
      </Tooltip>

      {/* Scroll to top button */}
      <Zoom in={showScrollTop}>
        <Fab
          size="small"
          aria-label="Volver arriba"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 28,
            width: 40,
            height: 40,
            minHeight: 40,
            background: 'rgba(15, 36, 64, 0.85)',
            color: '#fff',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            zIndex: 1200,
            '&:hover': {
              background: 'rgba(15, 36, 64, 0.95)',
            },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
        </Fab>
      </Zoom>
    </Box>
  );
}
